import React, { useState, useEffect, useRef } from 'react';
import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';

const MAX_LIMIT = 500000;
const STEP = 10000;

const DEGREE_LEVEL_OPTIONS = [
  { label: 'All Degree Levels', value: '' },
  { label: 'Undergraduate (BS / BE / BBA)', value: 'undergraduate' },
  { label: 'Graduate (MS / MBA / MPhil)', value: 'graduate' },
  { label: 'Doctoral (PhD)', value: 'doctoral' },
  { label: 'Associate & Diploma', value: 'associate' }
];

const ProgramsFilterPanel = ({
  filters,
  onFilterChange,
  onResetFilters,
  cities = [],
  fieldsOfStudy = [],
  activeFiltersCount = 0,
  isMobileDrawer = false,
  onCloseDrawer
}) => {
  const initialMin = filters.fee_min !== '' && filters.fee_min !== undefined ? Number(filters.fee_min) : 0;
  const initialMax = filters.fee_max !== '' && filters.fee_max !== undefined ? Number(filters.fee_max) : MAX_LIMIT;

  const [localMin, setLocalMin] = useState(initialMin);
  const [localMax, setLocalMax] = useState(initialMax);
  const debounceTimerRef = useRef(null);

  // Sync state when filters reset or change externally
  useEffect(() => {
    setLocalMin(filters.fee_min !== '' && filters.fee_min !== undefined ? Number(filters.fee_min) : 0);
  }, [filters.fee_min]);

  useEffect(() => {
    setLocalMax(filters.fee_max !== '' && filters.fee_max !== undefined ? Number(filters.fee_max) : MAX_LIMIT);
  }, [filters.fee_max]);

  const triggerFeeChange = (minVal, maxVal) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      onFilterChange('fee_range_dual', {
        fee_min: minVal > 0 ? minVal : '',
        fee_max: maxVal < MAX_LIMIT ? maxVal : ''
      });
    }, 250);
  };

  const handleMinChange = (e) => {
    const value = Math.min(Number(e.target.value), localMax - STEP);
    setLocalMin(value);
    triggerFeeChange(value, localMax);
  };

  const handleMaxChange = (e) => {
    const value = Math.max(Number(e.target.value), localMin + STEP);
    setLocalMax(value);
    triggerFeeChange(localMin, value);
  };

  const formatFeeLabel = (min, max) => {
    if (min <= 0 && max >= MAX_LIMIT) return 'Any Fee';
    if (min <= 0) return `≤ PKR ${max.toLocaleString()}`;
    if (max >= MAX_LIMIT) return `≥ PKR ${min.toLocaleString()}`;
    return `PKR ${min.toLocaleString()} – ${max.toLocaleString()}`;
  };

  return (
    <div className={`bg-white rounded-2xl border border-slate-200 shadow-sm ${isMobileDrawer ? 'p-6' : 'p-5'}`}>
      <style>{`
        .dual-range-input {
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
        }
        .dual-range-input::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          pointer-events: auto;
          cursor: grab;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background-color: #ffffff;
          border: 3.5px solid #334155;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
          transition: transform 0.1s ease, box-shadow 0.1s ease;
        }
        .dual-range-input::-webkit-slider-thumb:hover {
          transform: scale(1.15);
          box-shadow: 0 2px 7px rgba(51, 65, 85, 0.4);
        }
        .dual-range-input::-webkit-slider-thumb:active {
          cursor: grabbing;
          transform: scale(1.2);
          background-color: #334155;
          border-color: #ffffff;
        }
        .dual-range-input::-moz-range-thumb {
          pointer-events: auto;
          cursor: grab;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background-color: #ffffff;
          border: 3.5px solid #334155;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
          transition: transform 0.1s ease, box-shadow 0.1s ease;
        }
        .dual-range-input::-moz-range-thumb:hover {
          transform: scale(1.15);
          box-shadow: 0 2px 7px rgba(51, 65, 85, 0.4);
        }
        .dual-range-input::-moz-range-thumb:active {
          cursor: grabbing;
          transform: scale(1.2);
          background-color: #334155;
          border-color: #ffffff;
        }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between pb-3.5 mb-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <FunnelIcon className="h-4 w-4 text-primary-600" />
          <h2 className="text-sm font-bold text-slate-900">Filters</h2>
          {activeFiltersCount > 0 && (
            <span className="inline-flex items-center justify-center h-4.5 px-1.5 rounded-full bg-primary-100 text-primary-800 text-[10px] font-bold">
              {activeFiltersCount}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {activeFiltersCount > 0 && (
            <button
              type="button"
              onClick={onResetFilters}
              className="text-xs font-semibold text-slate-500 hover:text-primary-600 transition-colors"
            >
              Reset
            </button>
          )}
          {isMobileDrawer && (
            <button
              type="button"
              onClick={onCloseDrawer}
              className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600"
              aria-label="Close filters"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {/* Degree Level Dropdown */}
        <div>
          <label htmlFor="degree-level-select" className="block text-xs font-bold text-slate-700 mb-1.5">
            Degree Level
          </label>
          <select
            id="degree-level-select"
            value={filters.degree_level || ''}
            onChange={(e) => onFilterChange('degree_level', e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs sm:text-sm text-slate-800 font-medium focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all cursor-pointer"
          >
            {DEGREE_LEVEL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Discipline / Field of Study */}
        <div>
          <label htmlFor="field-select" className="block text-xs font-bold text-slate-700 mb-1.5">
            Discipline
          </label>
          <select
            id="field-select"
            value={filters.field_id || ''}
            onChange={(e) => onFilterChange('field_id', e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs sm:text-sm text-slate-800 font-medium focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all cursor-pointer"
          >
            <option value="">All Disciplines</option>
            {fieldsOfStudy.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>

        {/* Campus City */}
        <div>
          <label htmlFor="city-select" className="block text-xs font-bold text-slate-700 mb-1.5">
            Campus City
          </label>
          <select
            id="city-select"
            value={filters.city || ''}
            onChange={(e) => onFilterChange('city', e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs sm:text-sm text-slate-800 font-medium focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all cursor-pointer"
          >
            <option value="">All Cities</option>
            {cities.map((c) => (
              <option key={c.id || c.name} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* Tuition Fee Dual Slider */}
        <div className="pt-1">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-slate-700">
              Tuition Fee
            </span>
            <span className="text-[11px] font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200">
              {formatFeeLabel(localMin, localMax)}
            </span>
          </div>

          <div className="relative h-7 flex items-center">
            {/* Inactive Track */}
            <div className="absolute w-full h-2 bg-slate-200 rounded-full pointer-events-none" />

            {/* Active Range Fill */}
            <div
              className="absolute h-2 bg-slate-700 rounded-full pointer-events-none"
              style={{
                left: `${(localMin / MAX_LIMIT) * 100}%`,
                width: `${Math.max(0, ((localMax - localMin) / MAX_LIMIT) * 100)}%`
              }}
            />

            {/* Min Range Slider */}
            <input
              type="range"
              min={0}
              max={MAX_LIMIT}
              step={STEP}
              value={localMin}
              onChange={handleMinChange}
              aria-label="Minimum tuition fee"
              className={`dual-range-input absolute left-0 w-full h-2 pointer-events-none appearance-none bg-transparent ${
                localMin > MAX_LIMIT - 50000 ? 'z-30' : 'z-20'
              }`}
            />

            {/* Max Range Slider */}
            <input
              type="range"
              min={0}
              max={MAX_LIMIT}
              step={STEP}
              value={localMax}
              onChange={handleMaxChange}
              aria-label="Maximum tuition fee"
              className="dual-range-input absolute left-0 w-full h-2 pointer-events-none appearance-none bg-transparent z-20"
            />
          </div>

          <div className="flex justify-between text-[10px] font-medium text-slate-400 mt-1">
            <span>PKR 0</span>
            <span>PKR 500k+</span>
          </div>
        </div>

        {/* Mobile Drawer Action */}
        {isMobileDrawer && (
          <div className="pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={onCloseDrawer}
              className="w-full py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-bold text-xs rounded-xl transition-colors shadow-xs text-center"
            >
              Apply Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramsFilterPanel;
